import { useState } from "react";
import { motion } from "framer-motion";
import { Palette, Rocket, Bot, Check } from "lucide-react";

const divisions = [
  {
    key: "brand",
    title: "BRAND",
    tagline: "Creative energy. Storytelling. Identity.",
    icon: Palette,
    color: "from-neon-orange via-neon-magenta to-neon-purple",
    glow: "rgba(255,94,0,0.35)",
    services: [
      "Brand Strategy",
      "Identity Design",
      "Content Production",
      "UI/UX Design",
      "Video Production",
    ],
  },
  {
    key: "growth",
    title: "GROWTH",
    tagline: "Demand engines. Lead velocity. Pipeline.",
    icon: Rocket,
    color: "from-neon-magenta via-neon-purple to-neon-blue",
    glow: "rgba(255,0,127,0.35)",
    services: [
      "SEO / GEO",
      "Performance Marketing",
      "Lead Generation",
      "Growth Campaigns",
      "Conversion Optimization",
    ],
  },
  {
    key: "automation",
    title: "AUTOMATION",
    tagline: "AI agents. CRM systems. Compounding ops.",
    icon: Bot,
    color: "from-neon-cyan via-neon-blue to-neon-purple",
    glow: "rgba(0,240,255,0.35)",
    services: [
      "CRM Automation",
      "AI Agents",
      "Chatbots",
      "Workflow Systems",
      "Business Automation",
    ],
  },
];

export default function Divisions() {
  const [active, setActive] = useState("brand");

  return (
    <section id="divisions" data-testid="divisions-section" className="section relative">
      <div className="container-x">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <p className="overline mb-4">Three Core Divisions</p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">
              Brand. Growth. <span className="text-gradient-warm">Automation.</span>
            </h2>
          </div>
          <p className="text-white/55 max-w-md">
            Hover any division to expand. Each operates independently — and amplifies
            the others when combined.
          </p>
        </div>

        {/* Expanding cards */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {divisions.map((d) => {
            const isActive = active === d.key;
            const Icon = d.icon;
            return (
              <motion.div
                key={d.key}
                data-testid={`division-card-${d.key}`}
                onMouseEnter={() => setActive(d.key)}
                onFocus={() => setActive(d.key)}
                tabIndex={0}
                animate={{
                  flexGrow: isActive ? 2 : 1,
                }}
                className={`relative glass rounded-3xl p-7 lg:p-9 overflow-hidden transition-all duration-500 cursor-pointer
                  ${isActive ? "lg:col-span-6 border-white/15" : "lg:col-span-3 border-white/[0.06]"}
                  min-h-[360px] lg:min-h-[460px] flex flex-col justify-between`}
                style={{
                  boxShadow: isActive ? `0 30px 90px -40px ${d.glow}` : "none",
                }}
              >
                <div
                  className={`absolute inset-0 -z-10 opacity-0 transition-opacity duration-500 ${
                    isActive ? "opacity-100" : ""
                  } bg-gradient-to-br ${d.color}`}
                  style={{ maskImage: "radial-gradient(60% 60% at 20% 0%, black, transparent)" }}
                />
                <div
                  className={`absolute -bottom-20 -right-20 w-72 h-72 rounded-full blur-3xl opacity-30 bg-gradient-to-tr ${d.color}`}
                />

                <div className="relative">
                  <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${d.color}`}>
                    <Icon size={24} className="text-white" />
                  </div>
                  <h3 className="mt-6 text-3xl lg:text-4xl font-extrabold tracking-tight">
                    {d.title}
                  </h3>
                  <p className="mt-2 text-white/65">{d.tagline}</p>
                </div>

                <motion.ul
                  initial={false}
                  animate={{
                    opacity: isActive ? 1 : 0.4,
                    height: "auto",
                  }}
                  className="relative mt-6 space-y-2.5"
                >
                  {d.services.map((s) => (
                    <li key={s} className="flex items-center gap-3 text-sm text-white/85">
                      <span className="w-5 h-5 rounded-full bg-white/10 border border-white/15 flex items-center justify-center">
                        <Check size={11} />
                      </span>
                      {s}
                    </li>
                  ))}
                </motion.ul>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
